import React, { Fragment } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import Mixin from 'components/common/Mixin'
import Media from 'components/common/Media'
import Theme from 'components/common/Theme/Theme'
import PromoTeaser from 'components/common/PromoTeaser'
import PhoneLink from 'components/common/PhoneLink'
import { formatPrice } from 'helpers/utils'
import { Block, Cell, Text, CallStore, StoreLink } from '../../../Common.styles'
import Kit from '../Kit'
import { CALL_STORE_FOR_PRICE_FIRST_TEXT } from 'constant'

const ProductBlock = ({
  product: {
    productName,
    brandImageUrl,
    skuImageUrl,
    totalPrice,
    qty = '',
    description,
    promotions = [],
    installationKits = []
  },
  className,
  myStore,
  storePhoneNumber = '',
  formattedStorePhoneNumber = '',
  showStoreLocatorModalInAppointmentMode,
  handleTAOSDateTimeUpdate
}) =>
  <Wrapper data-at-appointment-product>
    <ItemWrapper className={ className }>
      <Image data-at-appointment-product-img asset={ skuImageUrl } />
      <Info>
        <Logo data-at-appointment-product-logo src={ `${brandImageUrl}?$BOT-MyAppointments-Tire-Brand-w110-alpha$` } />
        <TireName data-at-appointment-product-name>{ productName }</TireName>
        <Size data-at-appointment-product-size>{ description }</Size>
        {
          promotions.map(({ promotionName, promotionId, promotionType, promotionRebate }, index) =>
            <PromoTeaser
              creative="tire-appointments"
              index={ index }
              item={ promotions[index] }
              key={ index }
              promotionId={ promotionId }
              promotionRebate={ promotionRebate }
              promotionType={ promotionType }>
              { promotionName }
            </PromoTeaser>
          )
        }
      </Info>
      <Block data-at-appointment-product-qty>
        <Text margin>Qty:</Text><Text>{ qty }</Text>
      </Block>
      <Cell>
        {
          totalPrice && Number(totalPrice) > 0 ?
            <Fragment >
              <Mobile><Text margin>Price:</Text></Mobile>
              <Text data-at-appointment-product-price bold>{ formatPrice(totalPrice) }</Text>
            </Fragment> :
            <Fragment>
              {
                myStore ?
                  <CallStore data-at-appointment-product-price>
                    { CALL_STORE_FOR_PRICE_FIRST_TEXT }
                    <Media.Mobile>
                      <StyledPhoneLink number={ storePhoneNumber } />
                    </Media.Mobile>
                    <Media.Desktop>
                      <CallStore>{ formattedStorePhoneNumber }</CallStore>
                    </Media.Desktop>
                  </CallStore> :
                  <StoreLink
                    data-at-appointment-product-price
                    prevent
                    to="/"
                    bolder
                    handler={ () => showStoreLocatorModalInAppointmentMode(handleTAOSDateTimeUpdate) }>
                    Find Store for Price
                  </StoreLink>
              }
            </Fragment>
        }
      </Cell>
    </ItemWrapper>
    {
      installationKits.map((kit, index) =>
        <StyledKit
          key={ index }
          kit={ kit }
          success />
      )
    }
  </Wrapper>

ProductBlock.propTypes = {
  className: PropTypes.string,
  formattedStorePhoneNumber: PropTypes.string,
  handleTAOSDateTimeUpdate: PropTypes.func,
  myStore: PropTypes.shape(),
  product: PropTypes.shape({
    productName: PropTypes.string,
    brandImageUrl: PropTypes.string,
    skuImageUrl: PropTypes.string,
    totalPrice: PropTypes.number,
    qty: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number
    ]),
    description: PropTypes.string,
    promotions: PropTypes.arrayOf(),
    installationKits: PropTypes.arrayOf()
  }),
  showStoreLocatorModalInAppointmentMode: PropTypes.func,
  storePhoneNumber: PropTypes.string
}

export default ProductBlock

const Wrapper = styled.div`
  border-top: 1px solid ${Theme.colors.light_grey};
  ${Mixin.responsive('padding', '30px 0', '20px 0')}

  &:first-child {
    border-top: none;
  }
`

const ItemWrapper = styled.div`
  background-color: ${Theme.colors.off_white};
  ${Mixin.clearFix()}
`

const StyledKit = styled(Kit)`
  ${Mixin.responsive('padding-top', '10px', '20px')}
`

const Image = styled.div`
  background: ${Theme.colors.white} url(${props => props.asset});
  background-position: center center;
  background-repeat: no-repeat;
  background-size: 100%, contain;
  ${Media.desktop`
    display: inline-block;
    width: 144px;
    height: 144px;
    vertical-align: top;
  `}
`

const Info = styled.div`
  ${Media.desktop`
    display: inline-block;
    width: 276px;
    padding-left: 24px;
    vertical-align: top;
  `}
`

const Size = styled.div`
  margin-bottom: 20px;
  line-height: 22px;
  text-transform: uppercase;
`

const TireName = styled.div`
  margin-bottom: 3px;
  color: ${Theme.colors.sub_grey};
  ${Mixin.font.GothamMedium(18, 500, 24)}
`

const Logo = styled.img`
  max-width: 110px;
`

const Mobile = styled.span`
  ${Mixin.mobileOnly()}
`

const StyledPhoneLink = styled(PhoneLink)`
  display: block;
  font-weight: 600;
  line-height: 22px;
`
